
import { REPORT_CORRECTION_SERVICE_STATUS, STEPS_FOR_EDITING } from "@/constants/services";
import axios from "@/plugins/http";
import { SMCases, SMDocumentsV2, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { computed, defineComponent, getCurrentInstance, nextTick, ref, watch } from "vue-demi";

export default defineComponent({
  name: "DocumentsTree",

  components: {
    ActionItems: () => import("@/modules/Documents/components/ActionItems.vue"),
    ActionItem: () => import("@/modules/Documents/components/ActionItem.vue"),
    // FileView: () => import("@/modules/Documents/FileView.vue"),
    // FilePreview: () => import("@/modules/Documents/FilePreview.vue"),
    // UploaderPopup: () => import("@/modules/Documents/UploaderPopup.vue"),
    // ParsedDocumentPopup: () => import("@/modules/Documents/ParsedDocumentPopup.vue"),
    // NewConfirmPopup: () => import("@/modules/Documents/NewConfirmPopup.vue"),
    PDFViewer: () => import("@/modules/Documents/PDFViewer.vue"),
    DocumentsTreeNode: () => import("@/modules/Documents/DocumentsTreeNode.vue"),
    DocumentsTreeExpertActions: () => import("@/modules/Documents/DocumentsTreeExpertActions.vue"),
    FolderView: () => import("@/modules/Documents/FolderView.vue"),
    FolderViewList: () => import("@/modules/Documents/FolderViewList.vue"),
    Select: () => import("@/modules/Notifications/Select/Select.vue"),
    FileItem: () => import("@/modules/Documents/FileItem.vue"),
  },

  props: {
    view: {
      type: String as () => "" | "expert-company" | "employee" | "company" | "case",
      default: "",
    },
  },

  setup(props) {
    const instance = getCurrentInstance()?.proxy;
    const route = instance?.$route;
    const router = instance?.$router;

    const routeParamsId = computed(() => Number(route.params.id ?? 0));
    const caseId = computed(() => routeParamsId.value);
    const companyId = computed(() => Number(route.params.company_id ?? 0));

    const isExpert = computed(() => SMUser.isExpert);

    const directoryId = ref(0);
    const rootDirectoryId = ref(0);
    const directoryName = ref("");
    const isCurrentFolderApproved = ref(false);
    const writeRoleAccess = ref("");

    const documentTree = ref(null);
    const folderCards = ref([]);
    const moveToFolderCards = ref([]);
    const fileCards = ref([]);
    const files = ref<File[]>([]);

    const fileType = ref("default");

    const isParsedFileType = computed(() => {
      return ["cos", "ielts", "pte"].includes(fileType.value);
    });

    const uploadButtonLabel = computed(() => {
      if (!fileCards.value.length) return "Upload";

      return isParsedFileType.value ? "Reupload" : "Upload more";
    });

    function formatDateFolderName(name: string): string {
      return name.replace(/\b(\d{2})\.(\d{2})\.(\d{4})\b/g, (_, d, m, y) => `${d}/${m}/${y}`);
    }

    function calculateBreadcrumbs(encodedPath: string): string[] {
      if (!encodedPath) return ["All Files"];

      const decodedPath = decodeURIComponent(encodedPath);

      const parts = decodedPath.split("/").filter(Boolean);

      const relevantParts = parts.slice(3);

      const formattedParts = relevantParts.map((part) => formatDateFolderName(part));

      return ["All Files", ...formattedParts];
    }

    const expandedNodes = ref<Record<string, boolean>>({});

    const generatedTree = ref([]);

    function expandParents(fullRoute: string) {
      if (!fullRoute) return;
      const parts = fullRoute.split("/").filter(Boolean);

      let acc = "";

      for (const part of parts) {
        acc = acc ? `${acc}/${part}` : part;
        expandedNodes.value[acc] = true;
      }
    }

    function convertNullBool({ Bool, Valid }: { Bool: boolean; Valid: boolean }) {
      if (!Valid) return false;

      return Bool;
    }

    // Breadcrumbs
    type Crumb = { label: string; fullRoute: string };

    const breadcrumbs = ref<Crumb[]>([{ label: "All Files", fullRoute: "" }]);

    function buildBreadcrumbs(fullRoute: string): Crumb[] {
      const root = (documentTree.value?.full_route as string) || "";

      const result: Crumb[] = [{ label: "All Files", fullRoute: "" }];

      if (!fullRoute) return result;

      const rel = fullRoute.startsWith(root)
        ? fullRoute.slice(root.length).replace(/^\/+/, "")
        : fullRoute.replace(/^\/+/, "");

      let acc = root.replace(/\/$/, "");

      for (const seg of rel.split("/").filter(Boolean)) {
        acc = `${acc}/${seg}`;

        result.push({ label: formatDateFolderName(decodeURIComponent(seg)), fullRoute: acc });
      }

      return result;
    }

    const isSelectedTreeItem = computed(() => {
      const last = breadcrumbs.value[breadcrumbs.value.length - 1]?.label?.toLowerCase();

      return (name: string) => name.toLowerCase() === last;
    });

    function generateTree() {
      // no tree
      if (!documentTree.value?.SubDirectory) {
        generatedTree.value = [];
        moveToFolderCards.value = [];

        return;
      }

      function buildTree(subdirectories) {
        if (!Array.isArray(subdirectories)) return [];

        return subdirectories.map((dir) => ({
          ID: dir.ID,
          name: dir.name,
          displayName: formatDateFolderName(dir.name),
          full_route: dir.full_route,
          children: buildTree(dir.SubDirectory),
        }));
      }

      generatedTree.value = buildTree(documentTree.value.SubDirectory);

      // root level folder cards
      moveToFolderCards.value =
        (documentTree.value.SubDirectory || []).map((dir) => ({
          ID: dir.ID,
          name: dir.name,
          displayName: formatDateFolderName(dir.name),
          type: "folder",
          full_route: dir.full_route,
          is_approved: convertNullBool(dir.is_approved),
          is_filled: dir.is_filled,
        })) || [];
    }

    const objectType = computed<"case" | "employee" | "company" | "">(() => {
      if (props.view === "expert-company") {
        return "company";
      }

      if (props.view === "company") {
        return "company";
      }

      if (props.view === "employee") {
        return "employee";
      }

      if (props.view === "case") {
        return "case";
      }

      return "";
    });

    const objectId = computed(() => {
      if (props.view === "expert-company") {
        return companyId.value;
      }

      if (props.view === "company") {
        if (SMUser.isExpert) return companyId.value;

        return SMUser.companyId;
      }

      if (props.view === "employee") {
        if (SMUser.isEmployee) return SMUser.currentId;

        return routeParamsId.value;
      }

      if (props.view === "case") {
        return caseId.value;
      }

      return 0;
    });

    const isCase = computed(() => objectType.value === "case");

    const isDocumentsTreeLoading = ref(false);

    async function fetchDocumentsTree() {
      if (!objectId.value) return console.log("no object id");

      try {
        const { data } = await axios.get(
          API_ROUTES.DOCUMENTS_DIRECTORY(objectId.value, objectType.value)
        );

        documentTree.value = data || {};
        rootDirectoryId.value = data.ID || 0;

        generateTree();
      } catch (error) {
        console.error(`Error fetching documents:`, error);
      }
    }

    // const isUploadPopupOpen = ref(false);

    // function toggleUploadPopup(value: boolean) {
    //   isUploadPopupOpen.value = value;
    // }

    const currentRoute = ref(
      route.query.path ? decodeURIComponent(route.query.path as string) : ""
    );

    const filePath = computed(() => currentRoute.value);

    function normalizeFullRoute(fragmentOrFull: string): string {
      const root = (documentTree.value?.full_route as string) || "";

      if (!fragmentOrFull) return "";

      if (root && fragmentOrFull.startsWith(root)) return fragmentOrFull;

      const left = root.replace(/\/$/, "");
      const right = fragmentOrFull.replace(/^\//, "");

      return right ? `${left}/${right}` : left;
    }

    function normaliseForCompare(s: string) {
      return (s || "").replace(/\/+$/, "");
    }
    function routesEqual(a: string, b: string) {
      return normaliseForCompare(a) === normaliseForCompare(b);
    }

    function findDirectoryByFullRoute(
      target: string,
      current: any = documentTree.value
    ): any | null {
      if (!current) return null;
      if (routesEqual(current.full_route, target)) return current;

      for (const sub of current.SubDirectory || []) {
        if (routesEqual(sub.full_route, target)) return sub;
        const found = findDirectoryByFullRoute(target, sub);
        if (found) return found;
      }
      return null;
    }

    function generateTreeViewByRoute(fullRoute = "") {
      // Root
      if (!fullRoute) {
        folderCards.value =
          (documentTree.value?.SubDirectory || []).map((dir: any) => ({
            ID: dir.ID,
            name: dir.name,
            displayName: formatDateFolderName(dir.name),
            type: "folder",
            full_route: dir.full_route,
            is_approved: convertNullBool(dir.is_approved),
            is_filled: dir.is_filled,
          })) || [];

        fileCards.value =
          (documentTree.value?.Files || []).map((file: any) => ({
            ID: file.ID,
            name: file.name,
            url: file.url,
            displayName: formatDateFolderName(file.name),
            createdAt: file.CreatedAt,
            type: "file",
            s_3_path: file.s_3_path,
            preview_img_link: file.preview_img_link,
          })) || [];

        // Reset state
        directoryId.value = 0;
        directoryName.value = "";
        isCurrentFolderApproved.value = false;
        writeRoleAccess.value = "";
        fileType.value = "default";
        return;
      }

      const resolved = normalizeFullRoute(fullRoute);
      const directory = findDirectoryByFullRoute(resolved);
      if (!directory) return;

      directoryId.value = directory.ID;
      directoryName.value = directory.name;
      isCurrentFolderApproved.value = convertNullBool(directory.is_approved);
      writeRoleAccess.value = directory.write_role_access || "";

      if (directory.document_template?.file_type) {
        fileType.value = directory.document_template.file_type;
      } else {
        fileType.value = "default";
      }

      folderCards.value =
        (directory.SubDirectory || []).map((subDir: any) => ({
          ID: subDir.ID,
          name: subDir.name,
          displayName: formatDateFolderName(subDir.name),
          type: "folder",
          full_route: subDir.full_route,
          is_approved: convertNullBool(subDir.is_approved),
          is_filled: subDir.is_filled,
        })) || [];

      fileCards.value =
        (directory.Files || []).map((file: any) => ({
          ID: file.ID,
          name: file.name,
          url: file.url,
          displayName: formatDateFolderName(file.name),
          createdAt: file.CreatedAt,
          type: "file",
          s_3_path: file.s_3_path,
          preview_img_link: file.preview_img_link,
        })) || [];
    }

    function updateTreeFromRoute(fullRoute = "") {
      if (!documentTree.value) return;

      generateTree();

      generateTreeViewByRoute(fullRoute);
    }

    function update({ data, approved }: { data: any; approved: boolean }) {
      documentTree.value = data || {};
      rootDirectoryId.value = data.ID || 0;
      generateTree();
      isCurrentFolderApproved.value = approved;
    }

    async function init() {
      isDocumentsTreeLoading.value = true;

      if (props.view === "case") {
        await SMCases.fetchCaseTabs(caseId.value);
      }

      await fetchDocumentsTree();

      const initialPath = route.query.path ? decodeURIComponent(route.query.path as string) : "";

      currentRoute.value = initialPath;

      updateTreeFromRoute(initialPath);

      breadcrumbs.value = buildBreadcrumbs(initialPath || "");

      expandParents(normalizeFullRoute(initialPath));

      isDocumentsTreeLoading.value = false;
    }

    watch(
      objectId,
      async (newId, oldId) => {
        if (newId === oldId) return;

        if (newId && newId !== 0) {
          await init();
        }
      },
      { immediate: true }
    );

    async function openFolder(fullRoute: string) {
      if (currentRoute.value === fullRoute) return;

      currentRoute.value = fullRoute;

      expandParents(fullRoute);
      expandedNodes.value[fullRoute] = true;

      await router
        .replace({
          path: route.path,
          query: { path: encodeURIComponent(fullRoute) },
          replace: true,
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            console.log("Router navigation error:", err);
          }
        });

      await nextTick();

      breadcrumbs.value = buildBreadcrumbs(currentRoute.value);

      updateTreeFromRoute(fullRoute);
    }

    const isBreadcrumbBackArrowDisabled = computed(() => {
      if (!directoryId.value) return true;

      return false;
    });

    async function onBreadcrumbClick(bc: Crumb) {
      if (!bc) return;

      const fullPath = normalizeFullRoute(bc.fullRoute);

      if (!fullPath) {
        await router.replace({ path: route.path, query: null });
        currentRoute.value = "";
        updateTreeFromRoute("");
        breadcrumbs.value = buildBreadcrumbs("");

        // reset state
        directoryId.value = 0;
        directoryName.value = "";
        isCurrentFolderApproved.value = false;
        writeRoleAccess.value = "";
        fileType.value = "default";

        return;
      }

      if (routesEqual(currentRoute.value, fullPath)) {
        updateTreeFromRoute(fullPath);

        return;
      }

      currentRoute.value = fullPath;

      await router.replace({
        path: route.path,
        query: { path: encodeURIComponent(fullPath) },
        replace: true,
      });

      await nextTick();
      updateTreeFromRoute(fullPath);
      breadcrumbs.value = buildBreadcrumbs(fullPath);
    }

    async function onBreadcrumbBackArrowClick() {
      const idx = breadcrumbs.value.length - 2;
      if (idx < 0) return;
      await onBreadcrumbClick(breadcrumbs.value[idx]);
    }

    // File actions
    // const additionalData = ref(null);

    // function setAdditionalData(payload) {
    //   additionalData.value = payload;
    // }

    // const date = ref(null);

    // function setDate(payload) {
    //   date.value = payload;
    // }

    // const startDate = ref(null);
    // const endDate = ref(null);

    // function setDates(payload) {
    //   startDate.value = payload?.startDate || "";
    //   endDate.value = payload?.endDate || "";
    // }

    // const parsedContent = ref(null);

    const loading = ref(false);

    // const uploaderPopupKey = ref(0);

    // async function saveDocumentsToDirectory(files: File[]) {
    //   if (!directoryId.value) {
    //     console.log("No directory ID");
    //     return;
    //   }

    //   try {
    //     loading.value = true;

    //     const formData = new FormData();

    //     files.forEach((file) => formData.append("file", file));

    //     if (additionalData.value)
    //       formData.append("additional_data", JSON.stringify(additionalData.value));

    //     if (parsedContent.value) {
    //       formData.append("document_data", JSON.stringify(parsedContent.value));
    //     } else if (date.value) {
    //       formData.append("document_data", JSON.stringify({ date: date.value }));
    //     } else if (startDate.value && endDate.value) {
    //       formData.append("document_data", JSON.stringify({ start_date: startDate.value, end_date: endDate.value }));
    //     }

    //     await axios.post(API_ROUTES.DOCUMENTS_UPLOAD_TO_DIRECTORY(directoryId.value), formData, {
    //       headers: { "Content-Type": "multipart/form-data" },
    //     });

    //     await fetchDocumentsTree();

    //     updateTreeFromRoute(currentRoute.value);

    //     // isUploadPopupOpen.value = false;

    //     // uploaderPopupKey.value += 1;
    //   } catch (error) {
    //     console.log("Upload failed", error);
    //   } finally {
    //     loading.value = false;
    //   }
    // }

    // const onParsedDataOk = (payload: any) => {
    //   parsedContent.value = payload;

    //   saveDocumentsToDirectory(files.value);
    // };

    async function onFileDelete(fileId: number) {
      if (!fileId) return;

      if (!confirm("Are you sure?")) return;

      try {
        await axios.delete(API_ROUTES.DOCUMENTS_FILE(fileId));

        await fetchDocumentsTree();

        updateTreeFromRoute(currentRoute.value);
      } catch (error) {
        console.log("Failed to delete file", error);
      }
    }

    async function downloadAll() {
      const dirId = directoryId.value ? directoryId.value : rootDirectoryId.value;

      let name = "archive.zip";

      try {
        const response = await axios.get(API_ROUTES.DOCUMENTS_DIRECTORY_DOWNLOAD_ALL(dirId), {
          responseType: "arraybuffer",
        });

        if (!response) return;

        const contentDisposition = response.headers["content-disposition"];
        name = await SMDocumentsV2.getFileNameContentDisposition(contentDisposition);
        if (directoryName.value) name = `${directoryName.value} ${name}`;

        const blob = new Blob([response.data], { type: "application/zip" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Failed to download files", error);
      }
    }

    // PDF Preview
    const fileIdViewer = ref(0);
    const isPdfViewerOpen = ref(false);
    const selectedFileIndex = ref(0);
    const pdfViewerKey = ref(0);
    const pdfViewerLink = ref("");
    const pdfViewerName = ref("");

    function togglePdfViewer(value: boolean) {
      if (!value) fileIdViewer.value = 0;

      isPdfViewerOpen.value = value;
    }

    const showToggleFiles = computed(() => fileCards.value.length > 1);

    async function onToggleFile({
      index,
      direction,
    }: {
      index: number;
      direction: "prev" | "next";
    }) {
      const total = fileCards.value.length;
      if (!total) return;
      const normalise = (i: number) => ((i % total) + total) % total;
      const newIndex = direction === "next" ? normalise(index + 1) : normalise(index - 1);
      const f = fileCards.value[newIndex];
      if (!f) return;
      selectedFileIndex.value = newIndex;
      fileIdViewer.value = f.ID;
      pdfViewerLink.value = f.url;
      pdfViewerName.value = f.displayName || f.name;
      pdfViewerKey.value += 1;
    }

    function onFileView(fileId: number) {
      if (!fileId) return;

      const idx = fileCards.value.findIndex((f) => f.ID === fileId);
      selectedFileIndex.value = idx >= 0 ? idx : 0;

      fileIdViewer.value = fileId;

      togglePdfViewer(true);
    }

    async function onFileDownload(fileId: number) {
      try {
        const file = await SMDocumentsV2.getFileData(fileId);

        if (!file) return;

        const { link, name } = file;

        SMDocumentsV2.downloadFile({ link, name });
      } catch (error) {
        console.log("Failed to download file:", error);
      }
    }

    // Popups
    // const isParsedDocumentPopup = ref(false);

    // function openParsedDocumentPopup(payload: File[]) {
    //   files.value = payload;

    //   // isUploadPopupOpen.value = false;

    //   isParsedDocumentPopup.value = true;
    // }

    // function toggleParsedDocumentPopup(value: boolean) {
    //   isParsedDocumentPopup.value = value;
    // }

    // async function onParsedDocumentReupload() {
    //   files.value = [];
    //   parsedContent.value = null;
    //   additionalData.value = null;
    //   date.value = null;

    //   toggleParsedDocumentPopup(false);

    //   uploaderPopupKey.value += 1;

    //   // await nextTick();

    //   // toggleUploadPopup(true);
    // }

    // const isConfirmPopupOpen = ref(false);

    // function toggleConfirmPopup(value: boolean) {
    //   isConfirmPopupOpen.value = value;
    // }

    // function onConfirmOk() {
    //   toggleConfirmPopup(false);
    // }

    // function onConfirmCancel() {
    //   toggleConfirmPopup(false);

    //   // toggleUploadPopup(false);

    //   toggleParsedDocumentPopup(false);

    //   files.value = [];

    //   uploaderPopupKey.value += 1;
    // }

    // const onParsedDataCancel = () => {
    //   toggleConfirmPopup(true);
    // };

    // function onUploaderCancel(hasChange: boolean) {
    //   hasChange ? toggleUploadPopup(false) : toggleConfirmPopup(true);
    // }

    const isAdditionalDocument = computed(() => {
      return directoryName.value === "Additional documents";
    });

    // Upload Restrictions
    const canServicesUploadAndDelete = computed(() => {
      const caseStepName = SMCases.caseStepName;

      const caseStatus = SMCases.caseStatus;

      if (
        STEPS_FOR_EDITING.includes(caseStepName) ||
        caseStatus == REPORT_CORRECTION_SERVICE_STATUS
      ) {
        return writeRoleAccess.value.includes(SMUser.currentRole);
      } else {
        return false;
      }
    });

    const canComplianceUpload = computed(() => {
      if (SMUser.isComplianceBlocked) return false;

      return writeRoleAccess.value.includes(SMUser.currentRole);
    });

    const canComplianceDelete = computed(() => {
      return false;
    });

    const canUpload = computed(() => {
      if (!directoryId.value) return false;

      if (SMUser.isExpert) return true;

      if (objectType.value !== "case" && SMUser.isComplianceBlocked) return false;

      if (isAdditionalDocument.value) return true;

      if (objectType.value === "case") return canServicesUploadAndDelete.value;

      return canComplianceUpload.value;
    });

    const canDelete = computed(() => {
      if (SMUser.isExpert) return true;

      if (objectType.value !== "case" && SMUser.isComplianceBlocked) return false;

      if (isAdditionalDocument.value) return true;

      if (objectType.value === "case") return canServicesUploadAndDelete.value;

      return canComplianceDelete.value;
    });

    // Select view
    const selectedView = ref<"grid" | "list">("grid");

    interface Actions {
      updateValue: (v: "grid" | "list") => void;
    }

    const actions: Actions = {
      updateValue(v: "grid" | "list") {
        selectedView.value = v;
      },
    };

    function callAction(payload: { action: string; value: "grid" | "list" }) {
      try {
        actions[payload.action](payload.value);
      } catch {
        console.log(`Action "${payload.action}" is not defined`);
      }
    }

    const menuOptions = [
      {
        label: "Grid view",
        value: "grid",
        action: "updateValue",
      },
      {
        label: "List view",
        value: "list",
        action: "updateValue",
      },
    ];

    // Move to
    const isMoveToPopupOpen = ref(false);
    const isLoadingMoveToPopup = ref(false);

    const moveToRoute = ref<string>("");
    const moveToDirectoryId = ref<number>(0);
    const moveFileId = ref<number>(0);
    const moveToDirectoryName = ref<string>("");
    const moveToBreadcrumbs = ref<string[]>(["All Files"]);
    const moveToFileCards = ref<any[]>([]);

    const isSaveDisabledMoveToPopup = computed(() => moveToBreadcrumbs.value.length <= 1);

    function onCancelMoveToPopup() {
      isMoveToPopupOpen.value = false;
    }

    const moveFileName = ref("");

    function onFileMove(fileId: number, fileName: string) {
      moveFileId.value = fileId;

      moveFileName.value = fileName;

      isMoveToPopupOpen.value = true;
    }

    const isOkMoveToSuccessPopupOpen = ref(false);

    async function moveToDirectory() {
      try {
        isLoadingMoveToPopup.value = true;

        const fileId = moveFileId.value;
        const directoryId = moveToDirectoryId.value;

        if (!fileId || !directoryId) return console.log("no moveToDirectory data");

        await axios.put(API_ROUTES.DOCUMENTS_FILES_MOVE_TO(fileId, directoryId));

        isOkMoveToSuccessPopupOpen.value = true;

        await init();
      } catch {
        console.log("moveToDirectory failed");
      } finally {
        isLoadingMoveToPopup.value = false;

        isMoveToPopupOpen.value = false;
      }
    }

    const toFolderCard = (dir: any) => ({
      ID: dir.ID,
      name: dir.name,
      displayName: formatDateFolderName(dir.name),
      type: "folder",
      full_route: dir.full_route,
      is_approved: convertNullBool(dir.is_approved),
      is_filled: dir.is_filled,
    });

    const toFileCard = (file: any) => ({
      ID: file.ID,
      name: file.name,
      url: file.url,
      displayName: formatDateFolderName(file.name),
      createdAt: file.CreatedAt,
      type: "file",
      s_3_path: file.s_3_path,
      preview_img_link: file.preview_img_link,
    });

    function updateMoveToFromRoute(fullRoute = "") {
      if (!documentTree.value) return;

      if (!fullRoute) {
        moveToRoute.value = "";
        moveToDirectoryId.value = rootDirectoryId.value;
        moveToDirectoryName.value = "All Files";
        moveToFolderCards.value = (documentTree.value.SubDirectory || []).map(toFolderCard);
        moveToFileCards.value = (documentTree.value.Files || []).map(toFileCard);
        moveToBreadcrumbs.value = ["All Files"];
        return;
      }

      const directory = findDirectoryByFullRoute(fullRoute);
      if (!directory) return;

      moveToRoute.value = fullRoute;
      moveToDirectoryId.value = directory.ID;
      moveToDirectoryName.value = formatDateFolderName(directory.name || "");
      moveToFolderCards.value = (directory.SubDirectory || []).map(toFolderCard);
      moveToFileCards.value = (directory.Files || []).map(toFileCard);
      moveToBreadcrumbs.value = calculateBreadcrumbs(fullRoute);
    }

    watch(isMoveToPopupOpen, (open) => {
      if (open) {
        updateMoveToFromRoute("");
      }
    });

    async function onMoveToPopupOpenFolder(fullRoute: string) {
      if (!fullRoute || moveToRoute.value === fullRoute) return;

      updateMoveToFromRoute(fullRoute);
    }

    function onMoveToBreadcrumbClick(breadcrumb: string) {
      if (breadcrumb === "All Files") {
        updateMoveToFromRoute("");
        return;
      }

      const idx = moveToBreadcrumbs.value.indexOf(breadcrumb);
      if (idx === -1) return;

      const fullPath = moveToBreadcrumbs.value.slice(1, idx + 1).join("/");

      updateMoveToFromRoute(fullPath);
    }

    // "Move to" success popup
    async function onOkMoveToSuccessPopup() {
      // "View it now"
      isMoveToPopupOpen.value = false;

      if (moveToRoute.value) openFolder(moveToRoute.value);

      updateMoveToFromRoute("");

      isOkMoveToSuccessPopupOpen.value = false;
    }

    async function onCancelMoveToSuccessPopup() {
      // "Stay here"
      isMoveToPopupOpen.value = false;

      updateMoveToFromRoute("");

      isOkMoveToSuccessPopupOpen.value = false;
    }

    async function onOkMoveToPopup() {
      // "Move here"
      await moveToDirectory();
    }

    function openDocumentUpload() {
      if (!directoryId.value) return console.log("no directory ID");

      if (!canUpload.value) return;

      router.push(`${route.path}/upload/${directoryId.value}/${fileType.value}`);
    }

    return {
      isDocumentsTreeLoading,
      documentTree,
      generatedTree,
      folderCards,
      fileCards,
      breadcrumbs,
      directoryId,
      rootDirectoryId,
      directoryName,
      // isUploadPopupOpen,
      openFolder,
      onFileDelete,
      downloadAll,
      // saveDocumentsToDirectory,
      // toggleUploadPopup,
      isBreadcrumbBackArrowDisabled,
      onBreadcrumbBackArrowClick,
      onBreadcrumbClick,
      currentRoute,
      filePath,
      update,
      isSelectedTreeItem,
      isExpert,
      isPdfViewerOpen,
      togglePdfViewer,
      fileIdViewer,
      pdfViewerKey,
      pdfViewerLink,
      pdfViewerName,
      selectedFileIndex,
      showToggleFiles,
      onToggleFile,
      onFileView,
      onFileDownload,
      // setAdditionalData,
      // date,
      // setDate,
      // startDate,
      // endDate,
      // setDates,
      // onUploaderCancel,
      // openParsedDocumentPopup,
      // toggleParsedDocumentPopup,
      // onParsedDocumentReupload,
      // onConfirmCancel,
      // onConfirmOk,
      // toggleConfirmPopup,
      // isConfirmPopupOpen,
      // isParsedDocumentPopup,
      fileType,
      files,
      // onParsedDataOk,
      // uploaderPopupKey,
      expandedNodes,
      isCurrentFolderApproved,
      canUpload,
      canDelete,
      uploadButtonLabel,
      // onParsedDataCancel,
      isCase,
      loading,
      formatDateFolderName,
      menuOptions,
      callAction,
      selectedView,
      // Move to
      onFileMove,
      isMoveToPopupOpen,
      isSaveDisabledMoveToPopup,
      isLoadingMoveToPopup,
      onCancelMoveToPopup,
      onOkMoveToPopup,
      onMoveToPopupOpenFolder,
      onMoveToBreadcrumbClick,
      moveToRoute,
      moveToDirectoryId,
      moveToDirectoryName,
      moveToBreadcrumbs,
      moveToFolderCards,
      moveToFileCards,
      isOkMoveToSuccessPopupOpen,
      onOkMoveToSuccessPopup,
      onCancelMoveToSuccessPopup,
      moveFileName,
      openDocumentUpload,
    };
  },
});
